import { useState } from "react";
import { GRAPH_NODES, GRAPH_DIMENSIONS } from "../data/content";
import { Reveal, SectionHead } from "./Reveal";
import { useI18n } from "../i18n";

const POS = [
  { x: 200, y: 60 },
  { x: 355, y: 175 },
  { x: 295, y: 340 },
  { x: 105, y: 340 },
  { x: 45, y: 175 },
];

export default function KnowledgeGraph() {
  const { tr } = useI18n();
  const [active, setActive] = useState("place");

  return (
    <section id="graph" data-testid="knowledge-graph-section" className="py-24 lg:py-32 bg-[#F0FDF4]">
      <SectionHead
        index="§ 05"
        eyebrow="The Knowledge Graph"
        title="Person ↔ Place ↔ Organization ↔ Content ↔ Activity."
        sub="One photograph is a contributor, a village, a moment, a category and a trust level — surfaced everywhere it belongs without creating four independent copies."
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-14 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <svg viewBox="0 0 400 400" className="w-full max-w-lg mx-auto">
            {POS.map((a, i) =>
              POS.map((b, j) =>
                j > i ? (
                  <line key={`${i}-${j}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={GRAPH_NODES[i].id === active || GRAPH_NODES[j].id === active ? "#16A34A" : "#C7E8D2"}
                    strokeWidth={GRAPH_NODES[i].id === active || GRAPH_NODES[j].id === active ? 1.4 : 0.8}
                    strokeDasharray="4 4" style={{ transition: "stroke 0.4s" }} />
                ) : null
              )
            )}
            {GRAPH_NODES.map((node, i) => {
              const p = POS[i];
              const isActive = node.id === active;
              return (
                <g key={node.id} data-testid={`node-${node.id}`} onMouseEnter={() => setActive(node.id)} onClick={() => setActive(node.id)} style={{ cursor: "pointer" }}>
                  {isActive && <circle cx={p.x} cy={p.y} r="34" fill="#16A34A" className="node-pulse" />}
                  <circle cx={p.x} cy={p.y} r="26" fill={isActive ? "#16A34A" : "#FFFFFF"} stroke={isActive ? "#15803D" : "#64748B"} strokeWidth="1.2" style={{ transition: "fill 0.4s, stroke 0.4s" }} />
                  <text x={p.x} y={p.y + 3} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="8" letterSpacing="1" fill={isActive ? "#FFFFFF" : "#0F2557"}>
                    {node.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </Reveal>

        <div>
          <Reveal delay={0.1}>
            <div className="border border-[#86EFAC] rounded-3xl bg-white p-8">
              <div className="font-mono-wp text-xs uppercase tracking-[0.25em] text-[#15803D]">{tr("Active node")}</div>
              <div className="font-atlas text-3xl mt-2 text-[#0F2557]">{GRAPH_NODES.find((n) => n.id === active)?.label}</div>
              <div className="font-mono-wp text-xs text-[#4A5568] mt-2">{tr(GRAPH_NODES.find((n) => n.id === active)?.detail)}</div>
              <div className="atlas-dash my-6" />
              <div className="font-mono-wp text-[0.6rem] uppercase tracking-[0.25em] text-[#718096] mb-3">{tr("Every relationship carries")}</div>
              <div className="flex flex-wrap gap-2">
                {GRAPH_DIMENSIONS.map((d) => (
                  <span key={d} className="font-mono-wp text-[0.65rem] tracking-[0.15em] border border-[#C7E8D2] rounded-full px-3 py-1.5 text-[#4A5568] hover:border-[#16A34A] hover:text-[#15803D] transition-colors duration-300">{tr(d)}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-atlas italic text-xl text-[#4A5568] mt-8 leading-relaxed">
              {tr("“A bird photographed in Kadanad appears on the village portal, Indias.com, NaturePulse and the contributor's SocialPulse profile — one canonical record.”")}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
